import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, Save, FileText as FileTextIcon, Lock, CheckCircle2, Ban,
    Plus, Trash2, ArrowUp, ArrowDown, Download, FileSearch, Sparkles, Hash, Mail,
} from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type {
    InvoiceDocumentDetail, InvoiceItem, InvoiceLanguage, InvoiceCatalogItem, InvoiceStatus,
    PatientListItem,
} from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import PageHeader from '../ui/PageHeader';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import PatientPicker from './PatientPicker';
import CatalogPicker from './CatalogPicker';
import SendInvoiceDialog from './SendInvoiceDialog';
import {
    fmtDateDe, fmtMoney, fmtMoneyPlain, parseMoneyInput,
    statusLabelKey, statusTone, typeLabelKey, LANG_LABEL_KEY,
} from './invoiceHelpers';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors disabled:bg-brand-surface-2/60 disabled:text-brand-text-muted disabled:cursor-not-allowed';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

// Compact inline input used in the items table.
const cellInput = 'w-full px-2 py-1 text-sm bg-transparent border border-transparent text-brand-text rounded focus:outline-none focus:ring-1 focus:ring-brand-primary/40 focus:border-brand-primary/40 focus:bg-brand-surface disabled:text-brand-text-muted';

const InvoiceEditorPage: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { can } = useAuth();
    // Belege erstellen/bearbeiten erfordert die Capability "invoices-manage".
    // Ohne sie ist der Editor reine Ansicht (Bearbeiter mit nur "invoices").
    const canManage = can('invoices-manage');

    const [doc, setDoc] = useState<InvoiceDocumentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pickerNotice, setPickerNotice] = useState<string | null>(null);
    const [headerDirty, setHeaderDirty] = useState(false);
    const [showCatalog, setShowCatalog] = useState(false);
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [busyAction, setBusyAction] = useState<string | null>(null);

    // Local editable copy of header fields (issue_date, due_date, valid_until,
    // notes, billing_address). Items are mutated through dedicated endpoints
    // so we don't shadow them here.
    const [header, setHeader] = useState<Partial<InvoiceDocumentDetail>>({});

    const isFinalized = !!doc?.finalized_at;
    // Schreibgeschützt, wenn der Beleg finalisiert ist ODER der Benutzer keine
    // Bearbeitungsrechte hat.
    const viewOnly = !canManage;
    const readonly = isFinalized || viewOnly;

    // ----------------------------------------------------------------
    // Load (existing) OR create-on-open (new draft).
    // ----------------------------------------------------------------

    const fetchDoc = useCallback(async (docId: number) => {
        const res = await apiService.getInvoice(docId);
        const d = res.data.data;
        setDoc(d);
        setHeader({
            issue_date: d.issue_date,
            due_date: d.due_date,
            valid_until: d.valid_until,
            language: d.language,
            notes_header: d.notes_header,
            notes_footer: d.notes_footer,
            payment_terms_text: d.payment_terms_text,
            bank_details_text: d.bank_details_text,
            billing_address: { ...d.billing_address },
            internal_note: d.internal_note,
        });
        setHeaderDirty(false);
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError(null);
            setPickerNotice(null);
            try {
                if (id) {
                    await fetchDoc(Number(id));
                } else {
                    // Create-on-open flow: needs a patient.
                    const patientId = params.get('patient');
                    if (!patientId) {
                        // No patient yet — render a "pick patient first" stub
                        // (handled below by doc === null branch).
                        setDoc(null);
                        return;
                    }
                    const type = (params.get('type') as any) || 'invoice';
                    const language = (params.get('language') as InvoiceLanguage) || undefined;
                    const appointmentIds = params.get('appointment')
                        ? [Number(params.get('appointment'))]
                        : undefined;
                    try {
                        const res = await apiService.createInvoice({
                            patient_id: Number(patientId),
                            type,
                            language,
                            appointment_ids: appointmentIds,
                        });
                        const newId = res.data.data.id;
                        navigate(`/admin/invoices/${newId}`, { replace: true });
                    } catch (createErr: any) {
                        // Patient existiert nicht (mehr) oder andere Create-Probleme:
                        // statt harter Error-Seite zurück in den Picker-Stub.
                        const status = createErr?.response?.status;
                        const msg = createErr?.response?.data?.message;
                        const friendly = status === 404 || status === 400
                            ? (msg || t('billing.editor.patientNotFound', { id: patientId }))
                            : (msg || t('billing.editor.createError'));
                        // URL bereinigen, damit Reload nicht denselben Fehler triggert.
                        const cleanParams = new URLSearchParams(params);
                        cleanParams.delete('patient');
                        navigate(`/admin/invoices/new?${cleanParams.toString()}`, { replace: true });
                        setPickerNotice(friendly);
                        setDoc(null);
                    }
                }
            } catch (e: any) {
                setError(e?.response?.data?.message || t('billing.editor.loadError'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, params.get('patient')]);

    // ----------------------------------------------------------------
    // Header field handlers
    // ----------------------------------------------------------------

    const patchHeader = (patch: Partial<InvoiceDocumentDetail>) => {
        setHeader(prev => ({ ...prev, ...patch }));
        setHeaderDirty(true);
    };

    const patchAddress = (key: string, value: string) => {
        setHeader(prev => ({
            ...prev,
            billing_address: { ...(prev.billing_address || { name: '' }), [key]: value },
        }));
        setHeaderDirty(true);
    };

    const saveHeader = async () => {
        if (!doc || !headerDirty) return;
        try {
            setSaving(true);
            await apiService.updateInvoice(doc.id, header);
            await fetchDoc(doc.id);
            addToast(t('billing.editor.saved'), 'success');
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.saveFailed'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handlePickPatientForNew = (p: PatientListItem) => {
        // Bestehende Parameter (appointment, language) erhalten — bei
        // Retro-Rechnungen kommt der Termin-Param vom AppointmentModal mit.
        const next = new URLSearchParams(params);
        if (!next.get('type')) next.set('type', 'invoice');
        next.set('patient', String(p.id));
        navigate(`/admin/invoices/new?${next.toString()}`, { replace: true });
    };

    // ----------------------------------------------------------------
    // Items
    // ----------------------------------------------------------------

    const refreshAfterItemChange = async () => {
        if (!doc) return;
        await fetchDoc(doc.id);
    };

    const addFreeItem = async () => {
        if (!doc) return;
        try {
            await apiService.addInvoiceItem(doc.id, {
                source: 'free',
                label: t('billing.editor.freeItemLabel'),
                quantity: 1,
                unit: 'Stk',
                unit_price_cents: 0,
                vat_rate: 21,
            });
            await refreshAfterItemChange();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.editor.itemAddError'), 'error');
        }
    };

    const addFromCatalog = async (cat: InvoiceCatalogItem) => {
        if (!doc) return;
        try {
            const name = cat.name[doc.language] || cat.name.de || cat.name.es || cat.name.en;
            await apiService.addInvoiceItem(doc.id, {
                source: 'catalog',
                source_id: cat.id,
                code: cat.code,
                label: name,
                description: cat.description?.[doc.language] || '',
                quantity: 1,
                unit: cat.default_unit,
                unit_price_cents: cat.default_price_cents,
                vat_rate: cat.default_vat_rate,
            });
            setShowCatalog(false);
            await refreshAfterItemChange();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.editor.itemAddError'), 'error');
        }
    };

    const updateItem = async (item: InvoiceItem, patch: Partial<InvoiceItem>) => {
        if (!doc) return;
        try {
            await apiService.updateInvoiceItem(doc.id, item.id, patch);
            await refreshAfterItemChange();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.editor.itemSaveError'), 'error');
        }
    };

    const deleteItem = async (item: InvoiceItem) => {
        if (!doc) return;
        if (!window.confirm(t('billing.editor.confirmDeleteItem', { label: item.label || t('billing.editor.itemWithoutLabel') }))) return;
        try {
            await apiService.deleteInvoiceItem(doc.id, item.id);
            await refreshAfterItemChange();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.deleteFailed'), 'error');
        }
    };

    const moveItem = async (idx: number, dir: -1 | 1) => {
        if (!doc) return;
        const items = doc.items;
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const reordered = [...items];
        [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
        try {
            await apiService.reorderInvoiceItems(doc.id, reordered.map(it => it.id));
            await refreshAfterItemChange();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.sortFailed'), 'error');
        }
    };

    // ----------------------------------------------------------------
    // Status transitions
    // ----------------------------------------------------------------

    const withBusy = async (key: string, fn: () => Promise<void>) => {
        try {
            setBusyAction(key);
            await fn();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.actionFailed'), 'error');
        } finally {
            setBusyAction(null);
        }
    };

    const finalize = () => withBusy('finalize', async () => {
        if (!doc) return;
        if (headerDirty) {
            if (!window.confirm(t('billing.editor.confirmFinalizeDirty'))) return;
        }
        const res = await apiService.finalizeInvoice(doc.id);
        addToast(t('billing.editor.finalizedAs', { number: res.data.data.number }), 'success');
        await fetchDoc(doc.id);
    });

    const markPaid = () => withBusy('paid', async () => {
        if (!doc) return;
        await apiService.markInvoicePaid(doc.id, { paid_at: new Date().toISOString() });
        addToast(t('billing.editor.markedPaid'), 'success');
        await fetchDoc(doc.id);
    });

    const cancelInvoice = () => withBusy('cancel', async () => {
        if (!doc) return;
        const reason = window.prompt(t('billing.editor.cancelReasonPrompt')) || '';
        if (reason === null) return;
        const res = await apiService.cancelInvoice(doc.id, reason);
        const num = res.data.data.cancellation.number;
        addToast(t('billing.editor.cancellationCreated', { number: num }), 'success');
        await fetchDoc(doc.id);
    });

    const acceptOffer = () => withBusy('accept', async () => {
        if (!doc) return;
        await apiService.acceptOffer(doc.id);
        await fetchDoc(doc.id);
    });

    const rejectOffer = () => withBusy('reject', async () => {
        if (!doc) return;
        await apiService.rejectOffer(doc.id);
        await fetchDoc(doc.id);
    });

    const convertOffer = () => withBusy('convert', async () => {
        if (!doc) return;
        const res = await apiService.convertOfferToInvoice(doc.id);
        addToast(t('billing.editor.invoiceDraftCreated'), 'success');
        navigate(`/admin/invoices/${res.data.data.id}`);
    });

    const discardDraft = () => withBusy('delete', async () => {
        if (!doc) return;
        if (!window.confirm(t('billing.editor.confirmDiscardDraft'))) return;
        await apiService.deleteInvoice(doc.id);
        addToast(t('billing.editor.draftDeleted'), 'success');
        navigate('/admin/invoices');
    });

    const openPreview = async () => {
        if (!doc) return;
        try {
            const blob = await apiService.fetchInvoicePdf(doc.id, { preview: !isFinalized });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.pdfLoadError'), 'error');
        }
    };

    // ----------------------------------------------------------------
    // Rendering
    // ----------------------------------------------------------------

    // Summary numbers come straight from the server-computed doc totals.
    const totals = useMemo(() => {
        if (!doc) return { subtotal: 0, vat: 0, total: 0, breakdown: [] as { rate: number; vat_cents: number; net_cents: number }[] };
        return {
            subtotal: doc.subtotal_cents,
            vat: doc.vat_total_cents,
            total: doc.total_cents,
            breakdown: doc.vat_breakdown || [],
        };
    }, [doc]);

    // ----- Patient-required stub (new doc, no patient yet) -----

    if (!id && !doc && !loading && !error) {
        return (
            <div>
                <PageHeader
                    eyebrow={t('billing.eyebrow')}
                    title={t('billing.editor.newDocTitle', { type: t(typeLabelKey((params.get('type') as any) || 'invoice')) })}
                    description={t('billing.editor.newDocDescription')}
                    actions={
                        <Link
                            to="/admin/invoices"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                            {t('billing.back')}
                        </Link>
                    }
                />
                <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
                    {pickerNotice && (
                        <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            {pickerNotice}
                        </div>
                    )}
                    <label className={labelClass}>{t('billing.editor.patientLabel')}</label>
                    <PatientPicker value={null} onPick={handlePickPatientForNew} />
                    <p className="mt-2 text-[11px] text-brand-text-muted">
                        {t('billing.editor.pickPatientTip')}
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="p-12 text-center text-sm text-brand-text-muted">{t('billing.loading')}</div>;
    }

    if (error || !doc) {
        return (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800">
                {error || t('billing.editor.notFound')}
            </div>
        );
    }

    const numberDisplay = doc.number || t('billing.status.draft');

    return (
        <div>
            <PageHeader
                eyebrow={t(typeLabelKey(doc.type))}
                title={`${t(typeLabelKey(doc.type))} ${numberDisplay}`}
                description={
                    isFinalized
                        ? t('billing.editor.finalizedDescription', { date: fmtDateDe(doc.finalized_at?.slice(0, 10)) })
                        : t('billing.editor.editorDescription')
                }
                actions={
                    <>
                        <StatusPill tone={statusTone(doc.status as InvoiceStatus)} dot>
                            {t(statusLabelKey(doc.status as InvoiceStatus))}
                        </StatusPill>
                        <button
                            type="button"
                            onClick={openPreview}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                        >
                            <FileSearch className="h-3.5 w-3.5" strokeWidth={1.75} />
                            {isFinalized ? t('billing.editor.pdf') : t('billing.editor.preview')}
                        </button>
                        <Link
                            to="/admin/invoices"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-text-muted border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                            {t('billing.editor.listLink')}
                        </Link>
                    </>
                }
            />

            {/* Top notice for finalized docs / view-only users */}
            {readonly && (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-brand-accent/10 border border-brand-accent/30 px-4 py-3 text-sm text-brand-primary">
                    <Lock className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div>
                        <div className="font-medium">
                            {isFinalized ? t('billing.editor.finalizedBannerTitle') : t('billing.editor.viewOnlyBannerTitle')}
                        </div>
                        <div className="text-xs text-brand-text-muted">
                            {isFinalized ? t('billing.editor.finalizedBannerText') : t('billing.editor.viewOnlyBannerText')}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left / main column: address + items + footer text */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Recipient card */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-3">
                            {t('billing.editor.recipientTitle')}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                            <div className="sm:col-span-4">
                                <label className={labelClass}>{t('billing.editor.nameCompany')}</label>
                                <input type="text" value={header.billing_address?.name || ''} onChange={(e) => patchAddress('name', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelClass}>{t('billing.editor.nieNif')}</label>
                                <input type="text" value={header.billing_address?.nie || ''} onChange={(e) => patchAddress('nie', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                            <div className="sm:col-span-4">
                                <label className={labelClass}>{t('billing.editor.street')}</label>
                                <input type="text" value={header.billing_address?.street || ''} onChange={(e) => patchAddress('street', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                            <div className="sm:col-span-1">
                                <label className={labelClass}>{t('billing.editor.postalCode')}</label>
                                <input type="text" value={header.billing_address?.postal_code || ''} onChange={(e) => patchAddress('postal_code', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                            <div className="sm:col-span-1">
                                <label className={labelClass}>{t('billing.editor.country')}</label>
                                <input type="text" value={header.billing_address?.country || ''} onChange={(e) => patchAddress('country', e.target.value)} disabled={readonly} className={inputClass} maxLength={2} />
                            </div>
                            <div className="sm:col-span-3">
                                <label className={labelClass}>{t('billing.editor.city')}</label>
                                <input type="text" value={header.billing_address?.city || ''} onChange={(e) => patchAddress('city', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                            <div className="sm:col-span-3">
                                <label className={labelClass}>{t('billing.editor.email')}</label>
                                <input type="email" value={header.billing_address?.email || ''} onChange={(e) => patchAddress('email', e.target.value)} disabled={readonly} className={inputClass} />
                            </div>
                        </div>
                        <p className="mt-3 text-[11px] text-brand-text-muted">
                            {t('billing.editor.recipientHint')}
                        </p>
                    </section>

                    {/* Header note */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
                        <label className={labelClass}>{t('billing.editor.headerNoteLabel')}</label>
                        <textarea
                            value={header.notes_header || ''}
                            onChange={(e) => patchHeader({ notes_header: e.target.value })}
                            disabled={readonly}
                            rows={2}
                            placeholder={t('billing.editor.headerNotePlaceholder')}
                            className={inputClass}
                        />
                    </section>

                    {/* Items */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted">
                                {t('billing.editor.itemsTitle', { count: doc.items.length })}
                            </h3>
                            {!readonly && (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowCatalog(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        {t('billing.editor.fromCatalog')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addFreeItem}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                                    >
                                        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                                        {t('billing.editor.freeItem')}
                                    </button>
                                </div>
                            )}
                        </div>

                        {doc.items.length === 0 ? (
                            <EmptyState
                                icon={FileTextIcon}
                                title={t('billing.editor.itemsEmptyTitle')}
                                description={readonly
                                    ? t('billing.editor.itemsEmptyReadonly')
                                    : t('billing.editor.itemsEmptyEditable')}
                            />
                        ) : (
                            <ItemsTable
                                items={doc.items}
                                readonly={readonly}
                                onUpdate={updateItem}
                                onDelete={deleteItem}
                                onMove={moveItem}
                            />
                        )}
                    </section>

                    {/* Footer texts */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {doc.type === 'invoice' && (
                            <div className="sm:col-span-1">
                                <label className={labelClass}>{t('billing.editor.paymentTerms')}</label>
                                <textarea value={header.payment_terms_text || ''} onChange={(e) => patchHeader({ payment_terms_text: e.target.value })} disabled={readonly} rows={3} className={inputClass} />
                            </div>
                        )}
                        {doc.type === 'invoice' && (
                            <div className="sm:col-span-1">
                                <label className={labelClass}>{t('billing.editor.bankDetails')}</label>
                                <textarea value={header.bank_details_text || ''} onChange={(e) => patchHeader({ bank_details_text: e.target.value })} disabled={readonly} rows={3} placeholder={t('billing.editor.bankDetailsPlaceholder')} className={inputClass} />
                            </div>
                        )}
                        <div className="sm:col-span-2">
                            <label className={labelClass}>{t('billing.editor.footerText')}</label>
                            <textarea value={header.notes_footer || ''} onChange={(e) => patchHeader({ notes_footer: e.target.value })} disabled={readonly} rows={2} className={inputClass} />
                        </div>
                    </section>

                    {/* Internal note (not on PDF) */}
                    <section className="rounded-xl bg-brand-surface-2/40 border border-dashed border-brand-border p-4">
                        <label className={`${labelClass} flex items-center gap-1`}>
                            <Hash className="h-3 w-3" strokeWidth={1.75} aria-hidden />
                            {t('billing.editor.internalNote')}
                        </label>
                        <textarea value={header.internal_note || ''} onChange={(e) => patchHeader({ internal_note: e.target.value })} rows={2} className={inputClass} />
                    </section>
                </div>

                {/* Right / sticky column: meta + totals + actions */}
                <aside className="lg:col-span-1 space-y-4">

                    {/* Meta card */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-3">
                            {t('billing.editor.metaTitle')}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className={labelClass}>{t('billing.editor.patientLabel')}</label>
                                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-brand-text truncate">
                                            {doc.patient?.full_name || doc.billing_address.name}
                                        </div>
                                        <div className="text-xs text-brand-text-muted tabular-nums">{doc.patient_number}</div>
                                    </div>
                                    {doc.patient && (
                                        <Link
                                            to={`/admin/patients/${doc.patient.id}`}
                                            className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover cursor-pointer"
                                        >
                                            {t('billing.editor.patientFile')}
                                        </Link>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>{t('billing.editor.language')}</label>
                                <select value={header.language || 'de'} onChange={(e) => patchHeader({ language: e.target.value as InvoiceLanguage })} disabled={readonly} className={inputClass}>
                                    {(Object.keys(LANG_LABEL_KEY) as InvoiceLanguage[]).map(l => (
                                        <option key={l} value={l}>{t(LANG_LABEL_KEY[l])}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>{doc.type === 'offer' ? t('billing.editor.date') : t('billing.editor.invoiceDate')}</label>
                                <input type="date" value={header.issue_date || ''} onChange={(e) => patchHeader({ issue_date: e.target.value })} disabled={readonly} className={inputClass} />
                            </div>
                            {doc.type === 'invoice' && (
                                <div>
                                    <label className={labelClass}>{t('billing.editor.dueDate')}</label>
                                    <input type="date" value={header.due_date || ''} onChange={(e) => patchHeader({ due_date: e.target.value })} disabled={readonly} className={inputClass} />
                                </div>
                            )}
                            {doc.type === 'offer' && (
                                <div>
                                    <label className={labelClass}>{t('billing.editor.validUntilLabel')}</label>
                                    <input type="date" value={header.valid_until || ''} onChange={(e) => patchHeader({ valid_until: e.target.value })} disabled={readonly} className={inputClass} />
                                </div>
                            )}
                            {doc.source_offer_id && (
                                <p className="text-[11px] text-brand-text-muted">
                                    {t('billing.editor.createdFromOffer')} <Link to={`/admin/invoices/${doc.source_offer_id}`} className="underline cursor-pointer">{t('billing.editor.offerHash', { id: doc.source_offer_id })}</Link>
                                </p>
                            )}
                            {doc.cancels_id && (
                                <p className="text-[11px] text-brand-text-muted">
                                    {t('billing.editor.cancelsInvoice')} <Link to={`/admin/invoices/${doc.cancels_id}`} className="underline cursor-pointer">{t('billing.editor.invoiceHash', { id: doc.cancels_id })}</Link>
                                </p>
                            )}
                            {doc.canceled_by_id && (
                                <p className="text-[11px] text-rose-600">
                                    {t('billing.editor.canceledBy')} <Link to={`/admin/invoices/${doc.canceled_by_id}`} className="underline cursor-pointer">{t('billing.editor.cancellationHash', { id: doc.canceled_by_id })}</Link>
                                </p>
                            )}
                        </div>
                    </section>

                    {/* Totals */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-3">
                            {t('billing.editor.totalsTitle')}
                        </h3>
                        <dl className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-brand-text-muted">{t('billing.editor.net')}</dt>
                                <dd className="font-medium text-brand-text tabular-nums">{fmtMoney(totals.subtotal)}</dd>
                            </div>
                            {totals.breakdown.map(br => (
                                <div key={br.rate} className="flex justify-between text-xs">
                                    <dt className="text-brand-text-muted">{t('billing.editor.vatRate', { rate: br.rate })}</dt>
                                    <dd className="text-brand-text-muted tabular-nums">{fmtMoneyPlain(br.vat_cents)} €</dd>
                                </div>
                            ))}
                            <div className="pt-2 mt-2 border-t border-brand-border flex justify-between items-baseline">
                                <dt className="text-brand-text-muted text-xs uppercase tracking-wider">{t('billing.editor.total')}</dt>
                                <dd className="text-lg font-semibold text-brand-primary tabular-nums">{fmtMoney(totals.total)}</dd>
                            </div>
                        </dl>
                    </section>

                    {/* Action buttons */}
                    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5 space-y-2">
                        {!readonly && (
                            <>
                                <button
                                    type="button"
                                    onClick={saveHeader}
                                    disabled={saving || !headerDirty}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save className="h-4 w-4" strokeWidth={2} />
                                    {saving ? t('billing.editor.savingLabel') : headerDirty ? t('billing.editor.saveChanges') : t('billing.editor.savedLabel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={finalize}
                                    disabled={busyAction !== null || doc.items.length === 0}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-brand-primary border border-brand-primary hover:bg-brand-primary hover:text-white rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={doc.items.length === 0 ? t('billing.editor.finalizeDisabledTitle') : ''}
                                >
                                    <Lock className="h-4 w-4" strokeWidth={2} />
                                    {busyAction === 'finalize' ? t('billing.editor.finalizing') : t('billing.editor.finalize')}
                                </button>
                                <button
                                    type="button"
                                    onClick={discardDraft}
                                    disabled={busyAction !== null}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                >
                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    {t('billing.editor.discardDraft')}
                                </button>
                            </>
                        )}

                        {/* Offer-specific actions */}
                        {canManage && doc.type === 'offer' && doc.status === 'sent' && (
                            <>
                                <button type="button" onClick={acceptOffer} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                                    {t('billing.editor.offerAccepted')}
                                </button>
                                <button type="button" onClick={rejectOffer} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                    {t('billing.editor.offerRejected')}
                                </button>
                            </>
                        )}
                        {canManage && doc.type === 'offer' && doc.status === 'accepted' && (
                            <button type="button" onClick={convertOffer} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                {t('billing.editor.convertToInvoice')}
                            </button>
                        )}

                        {/* Invoice-specific actions */}
                        {canManage && doc.type === 'invoice' && doc.status === 'issued' && (
                            <>
                                <button type="button" onClick={markPaid} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                                    {t('billing.editor.markPaid')}
                                </button>
                                <button type="button" onClick={cancelInvoice} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                    <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    {t('billing.editor.cancelInvoice')}
                                </button>
                            </>
                        )}
                        {canManage && doc.type === 'invoice' && doc.status === 'paid' && (
                            <button type="button" onClick={cancelInvoice} disabled={busyAction !== null} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer disabled:opacity-50">
                                <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />
                                {t('billing.editor.cancelInvoice')}
                            </button>
                        )}

                        {canManage && doc.finalized_at && (
                            <button
                                type="button"
                                onClick={() => setShowSendDialog(true)}
                                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-brand-primary border border-brand-primary hover:bg-brand-primary hover:text-white rounded-md transition-colors cursor-pointer"
                            >
                                <Mail className="h-4 w-4" strokeWidth={2} />
                                {t('billing.editor.sendByMail')}
                            </button>
                        )}
                        {doc.finalized_at && doc.pdf_path && (
                            <button
                                type="button"
                                onClick={openPreview}
                                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                            >
                                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                                {t('billing.editor.downloadPdf')}
                            </button>
                        )}
                    </section>
                </aside>
            </div>

            {/* Catalog picker modal */}
            <CatalogPicker
                open={showCatalog}
                onClose={() => setShowCatalog(false)}
                onPick={addFromCatalog}
                language={doc.language}
            />

            {showSendDialog && (
                <SendInvoiceDialog
                    doc={doc}
                    onClose={() => setShowSendDialog(false)}
                    onSent={() => fetchDoc(doc.id)}
                />
            )}
        </div>
    );
};

// ====================================================================
// Items table (inline editable)
// ====================================================================

interface ItemsTableProps {
    items: InvoiceItem[];
    readonly: boolean;
    onUpdate: (item: InvoiceItem, patch: Partial<InvoiceItem>) => Promise<void>;
    onDelete: (item: InvoiceItem) => Promise<void>;
    onMove: (idx: number, dir: -1 | 1) => Promise<void>;
}

const ItemsTable: React.FC<ItemsTableProps> = ({ items, readonly, onUpdate, onDelete, onMove }) => {
    const { t } = useTranslation();
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full">
                <thead className="bg-brand-surface-2/60">
                    <tr className="text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted">
                        <th className="px-2 py-2 text-left w-10">{t('billing.editor.colNr')}</th>
                        <th className="px-2 py-2 text-left">{t('billing.editor.colLabel')}</th>
                        <th className="px-2 py-2 text-right w-20">{t('billing.editor.colQuantity')}</th>
                        <th className="px-2 py-2 text-left w-20">{t('billing.editor.colUnit')}</th>
                        <th className="px-2 py-2 text-right w-28">{t('billing.editor.colUnitPrice')}</th>
                        <th className="px-2 py-2 text-right w-16">{t('billing.editor.colVat')}</th>
                        <th className="px-2 py-2 text-right w-16">{t('billing.editor.colDiscount')}</th>
                        <th className="px-2 py-2 text-right w-28">{t('billing.editor.colTotal')}</th>
                        {!readonly && <th className="px-2 py-2 w-20" />}
                    </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                    {items.map((item, idx) => (
                        <ItemRow
                            key={item.id}
                            item={item}
                            idx={idx}
                            isFirst={idx === 0}
                            isLast={idx === items.length - 1}
                            readonly={readonly}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            onMove={onMove}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Per-row state buffers — committed onBlur so we don't fire one API call
// per keystroke. Reorder/delete still go straight through.
interface ItemRowProps {
    item: InvoiceItem;
    idx: number;
    isFirst: boolean;
    isLast: boolean;
    readonly: boolean;
    onUpdate: (item: InvoiceItem, patch: Partial<InvoiceItem>) => Promise<void>;
    onDelete: (item: InvoiceItem) => Promise<void>;
    onMove: (idx: number, dir: -1 | 1) => Promise<void>;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, idx, isFirst, isLast, readonly, onUpdate, onDelete, onMove }) => {
    const { t } = useTranslation();
    const [label, setLabel] = useState(item.label);
    const [description, setDescription] = useState(item.description);
    const [quantity, setQuantity] = useState(String(item.quantity));
    const [unit, setUnit] = useState(item.unit);
    const [priceText, setPriceText] = useState(fmtMoneyPlain(item.unit_price_cents));
    const [vat, setVat] = useState(String(item.vat_rate));
    const [discount, setDiscount] = useState(String(item.discount_percent || 0));

    // Re-sync local state when the parent doc reloads (totals or after API roundtrip).
    useEffect(() => { setLabel(item.label); }, [item.label]);
    useEffect(() => { setDescription(item.description); }, [item.description]);
    useEffect(() => { setQuantity(String(item.quantity)); }, [item.quantity]);
    useEffect(() => { setUnit(item.unit); }, [item.unit]);
    useEffect(() => { setPriceText(fmtMoneyPlain(item.unit_price_cents)); }, [item.unit_price_cents]);
    useEffect(() => { setVat(String(item.vat_rate)); }, [item.vat_rate]);
    useEffect(() => { setDiscount(String(item.discount_percent || 0)); }, [item.discount_percent]);

    const commit = (patch: Partial<InvoiceItem>) => onUpdate(item, patch);

    return (
        <tr className="hover:bg-brand-surface-2/30">
            <td className="px-2 py-2 text-sm text-brand-text-muted tabular-nums align-top pt-3">{idx + 1}</td>
            <td className="px-2 py-1 align-top">
                <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onBlur={() => label !== item.label && commit({ label })}
                    disabled={readonly}
                    placeholder={t('billing.editor.labelPlaceholder')}
                    className={cellInput + ' font-medium'}
                />
                <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => description !== item.description && commit({ description })}
                    disabled={readonly}
                    placeholder={t('billing.editor.descriptionPlaceholder')}
                    className={cellInput + ' text-xs text-brand-text-muted'}
                />
            </td>
            <td className="px-2 py-1 align-top">
                <input
                    type="number"
                    step="0.5" min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    onBlur={() => Number(quantity) !== item.quantity && commit({ quantity: Number(quantity) || 0 })}
                    disabled={readonly}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1 align-top">
                <input
                    type="text"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    onBlur={() => unit !== item.unit && commit({ unit })}
                    disabled={readonly}
                    className={cellInput}
                />
            </td>
            <td className="px-2 py-1 align-top">
                <input
                    type="text"
                    inputMode="decimal"
                    value={priceText}
                    onChange={(e) => setPriceText(e.target.value)}
                    onBlur={() => {
                        const cents = parseMoneyInput(priceText);
                        if (cents !== item.unit_price_cents) commit({ unit_price_cents: cents });
                        else setPriceText(fmtMoneyPlain(item.unit_price_cents));
                    }}
                    disabled={readonly}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1 align-top">
                <input
                    type="number"
                    step="1" min="0" max="100"
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                    onBlur={() => Number(vat) !== item.vat_rate && commit({ vat_rate: Number(vat) || 0 })}
                    disabled={readonly}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-1 align-top">
                <input
                    type="number"
                    step="1" min="0" max="100"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    onBlur={() => Number(discount) !== item.discount_percent && commit({ discount_percent: Number(discount) || 0 })}
                    disabled={readonly}
                    className={cellInput + ' text-right tabular-nums'}
                />
            </td>
            <td className="px-2 py-2 text-sm text-right text-brand-text tabular-nums font-medium align-top pt-3">
                {fmtMoneyPlain(item.line_total_cents)}
            </td>
            {!readonly && (
                <td className="px-2 py-1 align-top">
                    <div className="flex items-center justify-end gap-0.5">
                        <button
                            type="button"
                            onClick={() => onMove(idx, -1)}
                            disabled={isFirst}
                            className="p-1 text-brand-text-muted hover:text-brand-text rounded disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            title={t('billing.editor.moveUp')}
                            aria-label={t('billing.editor.moveItemUp')}
                        >
                            <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                            type="button"
                            onClick={() => onMove(idx, 1)}
                            disabled={isLast}
                            className="p-1 text-brand-text-muted hover:text-brand-text rounded disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            title={t('billing.editor.moveDown')}
                            aria-label={t('billing.editor.moveItemDown')}
                        >
                            <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(item)}
                            className="p-1 text-rose-600 hover:bg-rose-50 rounded cursor-pointer"
                            title={t('billing.editor.deleteItem')}
                            aria-label={t('billing.editor.deleteItem')}
                        >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                    </div>
                </td>
            )}
        </tr>
    );
};

export default InvoiceEditorPage;
